import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Reusable multi-select dropdown — replaces the inline <details>+checkboxes
// pattern that the task-assignees UI used to use. A single button shows the
// current selection count; clicking opens a popover with each option as a
// click-to-toggle row. Closes on outside-click and Escape.
//
// Selection state lives in the parent; this component just emits onChange
// when an option is toggled.
import { useEffect, useMemo, useRef, useState } from "react";
export default function MultiSelect({ options, values, onChange, renderTrigger, placeholder = "Select people", className, disabled, }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    // Close on outside click / Escape so the popover behaves like a real menu.
    useEffect(() => {
        if (!open)
            return;
        function onDocClick(e) {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        function onKey(e) {
            if (e.key === "Escape")
                setOpen(false);
        }
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    // Map values → option objects so the trigger label can show names instead
    // of bare ids. Sort by label for stable display.
    const selected = useMemo(() => options
        .filter((o) => values.includes(o.id))
        .sort((a, b) => a.label.localeCompare(b.label)), [options, values]);
    function toggle(id) {
        onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);
    }
    return (_jsxs("div", { className: `multi-select ${className ?? ""}`, ref: wrapRef, children: [_jsxs("button", { type: "button", className: "multi-select-trigger", onClick: () => setOpen((v) => !v), disabled: disabled, "aria-haspopup": "listbox", "aria-expanded": open, children: [renderTrigger ? (renderTrigger(selected)) : selected.length === 0 ? (_jsx("span", { className: "muted", children: placeholder })) : selected.length <= 2 ? (
                    // Few enough to inline the names directly.
                    _jsx("span", { children: selected.map((s) => s.label).join(", ") })) : (_jsxs("span", { children: [selected.length, " selected"] })), _jsx("span", { className: "multi-select-caret", children: "\u25BE" })] }), open && (_jsxs("div", { className: "multi-select-panel", role: "listbox", children: [options.length === 0 && (_jsx("div", { className: "multi-select-empty muted small", children: "No options." })), options.map((o) => {
                        const picked = values.includes(o.id);
                        return (_jsxs("button", { type: "button", className: `multi-select-option ${picked ? "selected" : ""}`, onClick: () => toggle(o.id), role: "option", "aria-selected": picked, children: [_jsx("span", { className: "multi-select-check", "aria-hidden": "true", children: picked ? "✓" : "" }), _jsx("span", { children: o.label })] }, o.id));
                    })] }))] }));
}
